import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  TALKER_WEEKDAYS,
  buildTalkerAvailabilityUpdate,
  defaultTalkerTimeZone,
  normalizeTalkerAvailability,
  talkerHourOptions,
} from '../../util/talkerAvailability';
import { weeklyAvailabilityRows } from '../../util/availabilitySummary';

import AvailableToListenToggle from '../AvailableToListenToggle/AvailableToListenToggle';
import ChangeOneDayEditor from '../ChangeOneDayEditor/ChangeOneDayEditor';

import css from './TalkerScheduleEditor.module.css';

const DAY_LABELS = weeklyAvailabilityRows({}).reduce((acc, row) => {
  acc[row.day] = row.label;
  return acc;
}, {});

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const WEEKENDS = ['sat', 'sun'];

const emptyDay = () => ({ on: false, startTime: '09:00', endTime: '17:00' });

const entriesFromDays = days =>
  TALKER_WEEKDAYS.flatMap(day => {
    const row = days[day];
    if (!row?.on || !row.startTime || !row.endTime) {
      return [];
    }
    return [{ dayOfWeek: day, startTime: row.startTime, endTime: row.endTime }];
  });

const daysFromPlan = plan => {
  const normalized = normalizeTalkerAvailability(plan);
  const byDay = Object.fromEntries(TALKER_WEEKDAYS.map(day => [day, emptyDay()]));
  normalized.entries.forEach(entry => {
    byDay[entry.dayOfWeek] = {
      on: true,
      startTime: entry.startTime,
      endTime: entry.endTime === '00:00' ? '24:00' : entry.endTime,
    };
  });
  return byDay;
};

const copySource = days => {
  const firstOn = TALKER_WEEKDAYS.find(day => days[day]?.on);
  return firstOn ? days[firstOn] : { on: true, startTime: '09:00', endTime: '17:00' };
};

const applyCopy = (days, targets, source) =>
  Object.fromEntries(
    TALKER_WEEKDAYS.map(day => [
      day,
      targets.includes(day) ? { ...source, on: true } : { ...days[day] },
    ])
  );

const hourLabel = (value, options) =>
  options.find(option => option.value === value)?.label || value;

const formatHours = (row, startOptions, endOptions) => {
  if (!row?.on) {
    return null;
  }
  return `${hourLabel(row.startTime, startOptions)}–${hourLabel(row.endTime, endOptions)}`;
};

/**
 * Weekly listener schedule: Set My Available Times.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.initialPlan]
 * @param {Object} [props.listing]
 * @param {Function} props.onSave
 * @param {boolean} [props.inProgress]
 * @param {string} [props.error]
 * @param {boolean} [props.hideHeading]
 * @param {'talker'|'listener'} [props.variant]
 * @param {string} [props.saveMessageId]
 * @param {string} [props.savingMessageId]
 * @returns {JSX.Element}
 */
const TalkerScheduleEditor = props => {
  const {
    initialPlan,
    listing,
    onSave,
    inProgress = false,
    error = null,
    hideHeading = false,
    variant = 'talker',
    saveMessageId = 'TalkerScheduleEditor.save',
    savingMessageId = 'TalkerScheduleEditor.saving',
    onSaveException,
    exceptionInProgress = false,
    exceptionError = null,
  } = props;
  const startOptions = useMemo(() => talkerHourOptions(false), []);
  const endOptions = useMemo(() => talkerHourOptions(true), []);
  const [timezone] = useState(
    () => normalizeTalkerAvailability(initialPlan).timezone || defaultTalkerTimeZone()
  );
  const [days, setDays] = useState(() => daysFromPlan(initialPlan));
  const [view, setView] = useState('list');
  const [editDay, setEditDay] = useState(null);
  const [pickDays, setPickDays] = useState(() => new Set());
  const [exceptionOpen, setExceptionOpen] = useState(false);

  const setDay = (day, patch) => {
    setDays(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  };

  const onSubmit = event => {
    event.preventDefault();
    if (typeof onSave !== 'function') {
      return;
    }
    onSave(
      buildTalkerAvailabilityUpdate({
        timezone,
        entries: entriesFromDays(days),
      })
    );
  };

  const source = copySource(days);
  const listingId = listing?.id?.uuid || listing?.id || null;

  const saveBar = (
    <div className={css.stickySave}>
      <button
        className={classNames(css.save, { [css.saveListener]: variant === 'listener' })}
        type="submit"
        disabled={inProgress}
      >
        <FormattedMessage id={inProgress ? savingMessageId : saveMessageId} />
      </button>
    </div>
  );

  return (
    <form
      className={classNames(css.root, { [css.listener]: variant === 'listener' })}
      onSubmit={onSubmit}
      data-testid="talker-schedule-editor"
      data-view={view}
    >
      {hideHeading ? (
        <h1 className={css.srOnly}>
          <FormattedMessage id="TalkerScheduleEditor.title" />
        </h1>
      ) : (
        <>
          <h1 className={css.title}>
            <FormattedMessage id="TalkerScheduleEditor.title" />
          </h1>
          <p className={css.lede}>
            <FormattedMessage id="TalkerScheduleEditor.lede" />
          </p>
        </>
      )}

      {listingId ? (
        <div className={css.availableCard}>
          <p className={css.availableLabel}>
            <FormattedMessage id="TalkerScheduleEditor.availableLabel" />
          </p>
          <AvailableToListenToggle listing={listing} variant="labeled" />
        </div>
      ) : null}

      {error ? (
        <p className={css.error} role="alert">
          {error}
        </p>
      ) : null}

      {view === 'copy' ? (
        <section className={css.subview} data-testid="copy-hours">
          <button type="button" className={css.back} onClick={() => setView('list')}>
            <FormattedMessage id="TalkerScheduleEditor.done" />
          </button>
          <h2 className={css.subhead}>
            <FormattedMessage id="TalkerScheduleEditor.copyHoursTitle" />
          </h2>
          <p className={css.copyHint}>
            <FormattedMessage
              id="TalkerScheduleEditor.copyHoursHint"
              values={{
                start: source.startTime,
                end: source.endTime,
              }}
            />
          </p>
          <div className={css.copyChoices}>
            <button
              type="button"
              className={css.copyChoice}
              onClick={() => {
                setDays(prev => applyCopy(prev, TALKER_WEEKDAYS, copySource(prev)));
                setView('list');
              }}
            >
              <FormattedMessage id="TalkerScheduleEditor.copyAllDays" />
            </button>
            <button
              type="button"
              className={css.copyChoice}
              onClick={() => {
                setDays(prev => applyCopy(prev, WEEKDAYS, copySource(prev)));
                setView('list');
              }}
            >
              <FormattedMessage id="TalkerScheduleEditor.copyWeekdays" />
            </button>
            <button
              type="button"
              className={css.copyChoice}
              onClick={() => {
                setDays(prev => applyCopy(prev, WEEKENDS, copySource(prev)));
                setView('list');
              }}
            >
              <FormattedMessage id="TalkerScheduleEditor.copyWeekends" />
            </button>
          </div>
          <p className={css.copyLede}>
            <FormattedMessage id="TalkerScheduleEditor.pickDays" />
          </p>
          <div className={css.pickGrid}>
            {TALKER_WEEKDAYS.map(day => (
              <label key={day} className={css.pickDay}>
                <input
                  type="checkbox"
                  checked={pickDays.has(day)}
                  onChange={event => {
                    setPickDays(prev => {
                      const next = new Set(prev);
                      if (event.target.checked) {
                        next.add(day);
                      } else {
                        next.delete(day);
                      }
                      return next;
                    });
                  }}
                />
                {DAY_LABELS[day]}
              </label>
            ))}
          </div>
          <button
            type="button"
            className={css.copyChoice}
            disabled={pickDays.size === 0}
            onClick={() => {
              const targets = [...pickDays];
              setDays(prev => applyCopy(prev, targets, copySource(prev)));
              setPickDays(new Set());
              setView('list');
            }}
          >
            <FormattedMessage id="TalkerScheduleEditor.applyPickedDays" />
          </button>
        </section>
      ) : null}

      {view === 'edit' && editDay ? (
        <section className={css.subview} data-testid="edit-day">
          <button type="button" className={css.back} onClick={() => setView('list')}>
            <FormattedMessage id="TalkerScheduleEditor.done" />
          </button>
          <h2 className={css.subhead}>{DAY_LABELS[editDay]}</h2>
          <label className={css.dayLabel}>
            <input
              type="checkbox"
              checked={!!days[editDay].on}
              onChange={event => setDay(editDay, { on: event.target.checked })}
            />
            <FormattedMessage id="TalkerScheduleEditor.availableThisDay" />
          </label>
          <div className={css.times}>
            <select
              className={css.select}
              value={days[editDay].startTime}
              disabled={!days[editDay].on}
              aria-label={`${DAY_LABELS[editDay]} start`}
              onChange={event => setDay(editDay, { startTime: event.target.value })}
            >
              {startOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className={css.to}>
              <FormattedMessage id="TalkerScheduleEditor.to" />
            </span>
            <select
              className={css.select}
              value={days[editDay].endTime}
              disabled={!days[editDay].on}
              aria-label={`${DAY_LABELS[editDay]} end`}
              onChange={event => setDay(editDay, { endTime: event.target.value })}
            >
              {endOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </section>
      ) : null}

      {view === 'list' ? (
        <>
          <section className={css.quickSetup} data-testid="quick-setup">
            <h2 className={css.quickTitle}>
              <FormattedMessage id="TalkerScheduleEditor.quickSetup" />
            </h2>
            <button type="button" className={css.quickLink} onClick={() => setView('copy')}>
              <FormattedMessage id="TalkerScheduleEditor.copyToAllDays" />
            </button>
            {typeof onSaveException === 'function' ? (
              <button
                type="button"
                className={css.exceptionLink}
                onClick={() => setExceptionOpen(true)}
              >
                <FormattedMessage id="TalkerScheduleEditor.changeOneDay" />
              </button>
            ) : null}
          </section>

          <ol className={css.days}>
            {TALKER_WEEKDAYS.map(day => {
              const row = days[day];
              const hours = formatHours(row, startOptions, endOptions);
              return (
                <li key={day} className={css.day}>
                  <button
                    type="button"
                    className={css.dayButton}
                    onClick={() => {
                      setEditDay(day);
                      setView('edit');
                    }}
                  >
                    <span className={css.dayName}>{DAY_LABELS[day]}</span>
                    <span className={css.dayMeta}>
                      {hours ? hours : <FormattedMessage id="TalkerScheduleEditor.unavailable" />}
                    </span>
                    <span className={css.chevron} aria-hidden="true">
                      ›
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <p className={css.repeatNote}>
            <FormattedMessage id="TalkerScheduleEditor.repeatsWeekly" />
          </p>
        </>
      ) : null}

      {saveBar}

      {exceptionOpen && typeof onSaveException === 'function' ? (
        <div className={css.exceptionScrim} role="presentation">
          <div
            className={css.exceptionModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-one-day-title"
          >
            <ChangeOneDayEditor
              timeZone={timezone}
              entries={entriesFromDays(days)}
              inProgress={exceptionInProgress}
              error={exceptionError}
              onCancel={() => setExceptionOpen(false)}
              onSubmit={payload => {
                return Promise.resolve(onSaveException(payload))
                  .then(() => setExceptionOpen(false))
                  .catch(() => {});
              }}
            />
          </div>
        </div>
      ) : null}
    </form>
  );
};

export default TalkerScheduleEditor;
